import { ReactNode, useId } from 'react';

import { CheckIcon, XIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { VoicePreview } from '@/components/atoms/VoicePreview/VoicePreview';
import { CommandEmpty } from '@/components/ui/command';
import {
  PopoverComboboxContent,
  PopoverComboboxGroup,
  PopoverComboboxInput,
  PopoverComboboxItem,
  PopoverComboboxList,
  PopoverComboboxTrigger,
} from '@/components/ui/custom/PopoverCombobox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { WaitingSoundName } from '@/hooks/useWaitingSounds';
import { capitalizeFirstLetter } from '@/util/util';

import PronunciationDictionaryToggle from './PronunciationDictionaryToggle';
import { TTSCardProps } from './types';
import { useElevenLabsKey, VerifyStatus } from './useElevenLabsKey';
import { useTTSVoiceOptions } from './useTTSVoiceOptions';
import WaitingSoundsSelector from './WaitingSoundsSelector';

const REGION_OPTIONS = [
  { value: 'global', label: 'Global' },
  { value: 'eu', label: 'EU' },
  { value: 'in', label: 'India' },
];

const VERIFY_STATUS_ICONS: Record<VerifyStatus, ReactNode> = {
  unverified: null,
  validating: <Spinner />,
  verified: <CheckIcon className="size-4 text-successful" />,
  invalid: <XIcon className="size-4 text-destructive" />,
};

/**
 * ElevenLabs Voice configuration component
 *
 * Provides form fields for:
 * - Voice selection (combobox with accent and preview)
 * - Speed slider (0.7-1.2)
 * - Stability slider (0-1)
 * - Similarity boost slider (0-1)
 * - Text normalization (radio group)
 * - Waiting sounds selector
 */
const ElevenLabsVoice = ({ basePath = 'config.tts_elevenlabs', strategy }: TTSCardProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const thinkingSoundName = useWatch({ control, name: 'config.thinking_sound.name' });
  const normalizationId = useId();
  const { isByoEnabled, verifyStatus, toggleByo } = useElevenLabsKey(basePath);
  const { voiceOptions, selectedVoice, selectVoice } = useTTSVoiceOptions('elevenlabs', {
    basePath,
    strategy,
  });

  return (
    <div className="space-y-6">
      {/* BYO ElevenLabs API Key; a strategy step takes the key from its speech model */}
      {!strategy && (
        <div className="space-y-3">
          <Switch
            label={t('integrations.voice_common.voice.use_own_elevenlabs')}
            description={t('integrations.voice_common.voice.use_own_elevenlabs_description')}
            variant="description"
            switchPosition="right"
            checked={isByoEnabled}
            onCheckedChange={toggleByo}
          />
          {isByoEnabled && (
            <>
              <FormField
                control={control}
                name={`${basePath}.api_key`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <InputGroup>
                        <InputGroupInput
                          {...field}
                          value={field.value ?? ''}
                          type="password"
                          placeholder={t(
                            'integrations.voice_common.voice.elevenlabs_api_key_placeholder'
                          )}
                        />
                        <InputGroupAddon align="inline-end">
                          {VERIFY_STATUS_ICONS[verifyStatus]}
                        </InputGroupAddon>
                      </InputGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`${basePath}.region`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('integrations.voice_common.voice.elevenlabs_region')}</FormLabel>
                    <FormControl>
                      <Select value={field.value ?? 'global'} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REGION_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      {t('integrations.voice_common.voice.elevenlabs_region_description')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </div>
      )}

      <FormField
        control={control}
        name={`${basePath}.voice_id`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.test_voice')}</FormLabel>
            <VoicePreview previewUrl={selectedVoice?.previewUrl} key={selectedVoice?.previewUrl} />
            <FormLabel>{t('integrations.voice_common.voice.voice_id')}</FormLabel>
            <FormControl>
              {/* Using custom combobox to handle voice selection */}
              <Popover>
                <PopoverTrigger asChild>
                  <PopoverComboboxTrigger
                    role="combobox"
                    className="w-full justify-between [&>span]:w-full"
                  >
                    <div className="flex min-w-0 items-center justify-between gap-2 text-left">
                      <span className="truncate">{selectedVoice?.label}</span>
                      <span className="shrink-0 text-xs text-foreground-muted">
                        {capitalizeFirstLetter(selectedVoice?.accent ?? undefined)}
                      </span>
                    </div>
                  </PopoverComboboxTrigger>
                </PopoverTrigger>
                <PopoverComboboxContent commandProps={{ shouldFilter: true }}>
                  <PopoverComboboxInput />
                  <PopoverComboboxList>
                    <CommandEmpty className="px-2 py-3 text-center text-sm text-foreground-muted">
                      {t('common.no_results')}
                    </CommandEmpty>
                    <PopoverComboboxGroup>
                      {voiceOptions.map((voice) => (
                        <PopoverComboboxItem
                          key={voice.value}
                          value={voice.value}
                          keywords={[voice.label, voice.accent || '']}
                          onSelect={() => selectVoice(voice.value)}
                          selected={field.value === voice.value}
                        >
                          <div className="flex w-full min-w-0 items-center justify-between gap-2">
                            <TruncatedText text={voice.label} className="min-w-0 flex-1" />
                            {voice.accent && (
                              <span className="shrink-0 text-xs text-foreground-muted">
                                {capitalizeFirstLetter(voice.accent)}
                              </span>
                            )}
                          </div>
                        </PopoverComboboxItem>
                      ))}
                    </PopoverComboboxGroup>
                  </PopoverComboboxList>
                </PopoverComboboxContent>
              </Popover>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.voice_settings.speed`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.speed')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                displayRealValue
                min={0.7}
                max={1.2}
                step={0.01}
                minLabel={t('integrations.voice_common.voice.slower')}
                maxLabel={t('integrations.voice_common.voice.faster')}
                value={[field.value ?? 1.0]}
                onValueChange={(values) =>
                  setValue(`${basePath}.voice_settings.speed`, values[0], { shouldDirty: true })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.speed_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.voice_settings.stability`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.stability')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                min={0}
                max={1}
                step={0.01}
                minLabel={t('integrations.voice_common.voice.more_variable')}
                maxLabel={t('integrations.voice_common.voice.more_stable')}
                value={[field.value ?? 0.5]}
                onValueChange={(values) =>
                  setValue(`${basePath}.voice_settings.stability`, values[0], { shouldDirty: true })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.stability_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.voice_settings.similarity_boost`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.similarity')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                minLabel={t('integrations.voice_common.voice.low')}
                maxLabel={t('integrations.voice_common.voice.high')}
                min={0}
                max={1}
                step={0.01}
                value={[field.value ?? 0.75]}
                onValueChange={(values) =>
                  setValue(`${basePath}.voice_settings.similarity_boost`, values[0], {
                    shouldDirty: true,
                  })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.similarity_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.apply_text_normalization`}
        render={({ field }) => (
          <FormItem className="space-y-3">
            <FormLabel>{t('integrations.voice_common.voice.text_normalization')}</FormLabel>
            <FormDescription>
              {t('integrations.voice_common.voice.text_normalization_description')}
            </FormDescription>
            <FormControl>
              <RadioGroup
                value={field.value || 'auto'}
                onValueChange={(value: 'auto' | 'on' | 'off') =>
                  setValue(`${basePath}.apply_text_normalization`, value, { shouldDirty: true })
                }
              >
                <RadioGroupItem
                  id={`${normalizationId}-auto`}
                  value="auto"
                  description={t(
                    'integrations.voice_common.voice.text_normalization_auto_description'
                  )}
                >
                  {t('integrations.voice_common.voice.text_normalization_auto')}
                </RadioGroupItem>
                <RadioGroupItem
                  id={`${normalizationId}-on`}
                  value="on"
                  description={t(
                    'integrations.voice_common.voice.text_normalization_on_description'
                  )}
                >
                  {t('integrations.voice_common.voice.text_normalization_on')}
                </RadioGroupItem>
                <RadioGroupItem
                  id={`${normalizationId}-off`}
                  value="off"
                  description={t(
                    'integrations.voice_common.voice.text_normalization_off_description'
                  )}
                >
                  {t('integrations.voice_common.voice.text_normalization_off')}
                </RadioGroupItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Dictionaries live in Moveo's provider account, so a customer's key cannot use them */}
      {!strategy?.speechModelId && (
        <PronunciationDictionaryToggle
          provider="elevenlabs"
          name={`${basePath}.pronunciation_dictionary_id`}
          versionName={`${basePath}.pronunciation_dictionary_version_id`}
        />
      )}

      {!strategy && (
        <WaitingSoundsSelector
          value={thinkingSoundName as WaitingSoundName | ''}
          onChange={(value) => setValue('config.thinking_sound.name', value, { shouldDirty: true })}
        />
      )}
    </div>
  );
};

export default ElevenLabsVoice;
